import { useEffect, useState, useCallback } from "react"
import { DataGrid, useGridApiRef } from '@mui/x-data-grid'
import Checkbox from "@mui/material/Checkbox"
import { BlockIcon, CheckIcon } from "../../../components/Icons/Icons"
import { useDispatch, useSelector } from "react-redux"
import Button from "../../../components/Button/Button"
import { useMyContextLoading } from "../../../components/LoadingContextProvider/LoadingContextProvider"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import { formatMoney } from "../../../utils/amount"
import ManualCreditReportModal from "./SelfEnrollmentManualModal"
import SelfEnrollmentUpdateDebtModal from "./SelfEnrollmentUpdateDebtModal"
import PopupWithActions from "../../../components/Popup/PopupWithAction"
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal"
import ActionIcon from "../../../assets/images/icons/ActionDotsIcon.svg"
import { validateForm } from "../../Contacts/add-contact/CreditSelect/helper"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import {
  getCreditOptions,
  createCreditReport,
  createCreditProfiles,
  getEnrollmentDebts,
  deleteEnrollmentDebt,
} from "../../../features/selfEnrollment/selfEnrollment"

dayjs.extend(utc)
dayjs.extend(timezone)

const getDefaultDate = () => dayjs().tz(dayjs.tz.guess()).toDate()

const SelfEnrollmentCreditTables = ({
  selectedCreditLines,
  setSelectedCreditLines,
  onNext,
  onBack,
  useManualEntry,
  popupOnRequest = false,
}) => {
  const dispatch = useDispatch()
  const { triggerLoading } = useMyContextLoading()
  const apiRef = useGridApiRef()
  const ineligibleApiRef = useGridApiRef()

  const {
    contactId,
    debts,
    creditOptions,
    creditReportId,
    debtsLoading,
  } = useSelector(state => state.selfEnrollment)

  const [isFirstModalPage, setIsFirstModalPage] = useState(true)
  const [debtsLoaded, setDebtsLoaded] = useState(false)

  const [creditReport, setCreditReport] = useState({
    fullName: "",
    reportDate: getDefaultDate(),
    creditScore: ""
  })

  const initialFieldSet = {
    creditReport: creditReportId,
    accountType: "",
    accountNumber: "",
    openedDate: getDefaultDate(),
    reportedDate: getDefaultDate(),
    closedDate: getDefaultDate(),
    lastPaymentDate: getDefaultDate(),
    accountStatus: "",
    isOpenAccount: "",
    creditorName: "",
    balanceAmount: "",
    pastDueAmount: "",
    creditLimit: "",
  }

  const [fields, setFields] = useState([initialFieldSet])
  const [errors, setErrors] = useState([])

  const [anchor, setAnchor] = useState(null)
  const [popupOpen, setPopupOpen] = useState(false)
  const [selectedRowForAction, setSelectedRowForAction] = useState(null)
  const [openDeleteModal, setOpenDeleteModal] = useState({ open: false, debtId: '' })
  const [openUpdate, setOpenUpdate] = useState({ open: false, creditLine: null })

  useEffect(() => {
    if (!creditOptions?.types?.length) {
      dispatch(getCreditOptions())
    }
  }, [])

  useEffect(() => {
    if (!debtsLoading) {
      setDebtsLoaded(true)
    }
  }, [debtsLoading])

  useEffect(() => {
    if (creditReportId) {
      setIsFirstModalPage(false)
    }
  }, [creditReportId])

  useEffect(() => {
    validateForm({ fields, setErrors })
  }, [fields])

  const handleCheckboxChange = useCallback((creditLine) => {
    setSelectedCreditLines((prev) => {
      const exists = prev?.find((line) => line._id === creditLine._id)
      if (exists) {
        return prev.filter((line) => line._id !== creditLine._id)
      }
      return prev ? [...prev, creditLine] : [creditLine]
    })
  }, [setSelectedCreditLines])

  const handleCreateCreditReport = async () => {
    triggerLoading(true)
    const result = await dispatch(createCreditReport({
      contactId,
      fullName: creditReport.fullName,
      reportDate: creditReport.reportDate,
      creditScore: creditReport.creditScore,
    }))
    triggerLoading(false)

    if (createCreditReport.fulfilled.match(result)) {
      setIsFirstModalPage(false)
      setFields([{ ...initialFieldSet, creditReport: result.payload.data._id }])
    } else {
      showSnackBar("error", result.payload || "Failed to create credit report")
    }
  }

  const handleCreateDebts = async () => {
    triggerLoading(true)
    const result = await dispatch(createCreditProfiles({
      contactId,
      creditReportId,
      profiles: fields.map(field => ({
        ...field,
        creditReport: creditReportId,
        balanceAmount: parseFloat(field.balanceAmount) || 0,
        pastDueAmount: parseFloat(field.pastDueAmount) || 0,
        creditLimit: parseFloat(field.creditLimit) || 0,
      })),
    }))
    triggerLoading(false)

    if (createCreditProfiles.fulfilled.match(result)) {
      setFields([{ ...initialFieldSet, creditReport: creditReportId }])
      setErrors([])
      await dispatch(getEnrollmentDebts({ contactId }))
    } else {
      showSnackBar("error", result.payload || "Failed to create debts")
    }
  }

  const handleActionClick = (event, creditLine) => {
    setAnchor(event.currentTarget)
    setSelectedRowForAction(creditLine)
    setPopupOpen(true)
  }

  const handleActionClose = () => {
    setAnchor(null)
    setPopupOpen(false)
    setSelectedRowForAction(null)
  }

  const handleEditClick = () => {
    setOpenUpdate({ open: true, creditLine: selectedRowForAction })
    handleActionClose()
  }

  const handleDeleteClick = () => {
    setOpenDeleteModal({ open: true, debtId: selectedRowForAction?._id })
    handleActionClose()
  }

  const removeDebt = async () => {
    if (!openDeleteModal?.debtId) return
    triggerLoading(true)
    const result = await dispatch(deleteEnrollmentDebt({ contactId, debtId: openDeleteModal.debtId }))
    triggerLoading(false)

    if (deleteEnrollmentDebt.fulfilled.match(result)) {
      setSelectedCreditLines((prev) => (prev || []).filter((line) => line._id !== openDeleteModal.debtId))
      showSnackBar("success", "The debt has been deleted")
    } else {
      showSnackBar("error", result.payload || "Failed to delete debt")
    }
  }

  const handleInputChange = (field, value) => {
    setCreditReport((prevData) => ({
      ...prevData,
      [field]: value,
    }))
  }

  const getColumns = () => [
    {
      field: 'select',
      headerName: '',
      width: 20,
      sortable: false,
      renderCell: (params) => (
        <Checkbox
          checked={!!selectedCreditLines?.find((line) => line._id === params.row._id)}
          onChange={() => handleCheckboxChange(params.row)}
          sx={{
            "&.Mui-checked": {
              color: "text.primary",
            },
          }}
        />
      ),
      headerCheckbox: false,
    },
    {
      field: 'creditorName',
      headerName: 'Creditor',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
          <div className="flex items-start w-full">
            <span className="flex-1 break-words text-wrap line-clamp-3 min-w-0 w-full break-all leading-[1.2] hyphens-auto">
              {params.row?.creditorName || params.row?.bankAccount}
            </span>
          </div>
      ),
    },
    {
      field: 'accountNumber',
      headerName: 'Account Number',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full text-wrap line-clamp-3 break-all leading-[1.2] hyphens-auto">
          {params.row?.accountNumber || 'N/A'}
        </div>
      ),
    },
    {
      field: 'accountType',
      headerName: 'Account Type',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full text-wrap line-clamp-4">
          {params.row?.accountType?.description || 'N/A'}
        </div>
      ),
    },
    {
      field: 'accountStatus',
      headerName: 'Account Status',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <div className="w-full text-wrap line-clamp-4">
          {params.row?.accountStatus?.description || 'N/A'}
        </div>
      ),
    },
    {
      field: 'balanceAmount',
      headerName: 'Balance Owed',
      flex: 1,
      sortable: false,
      renderCell: (params) => formatMoney(params.row?.balanceAmount),
    },
    {
      field: 'actions',
      headerName: '',
      width: 40,
      sortable: false,
      renderCell: (params) => (
        params.row?.sourceType === 'manual' ? (
          <div
            className="cursor-pointer p-2 flex justify-center items-center"
            onClick={(e) => handleActionClick(e, params.row)}
          >
            <img src={ActionIcon} alt="actions" className="w-4 h-4" />
          </div>
        ) : null
      ),
    },
  ]

  const eligibleData = debts?.eligible || []
  const ineligibleData = debts?.ineligible || []

  const dataGridSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
      '.MuiButtonBase-root[aria-haspopup="menu"]': {
        display: 'none'
      }
    },
    '& .MuiDataGrid-cell': {
      overflow: 'visible !important',
      position: 'relative',
    },
    '& .MuiDataGrid-virtualScroller': {
      minHeight: 300,
      overflow: 'visible !important',
    },
    '& .MuiDataGrid-main': {
      overflow: 'visible !important',
    },
  }

  return (
    <div className="space-y-6 w-full">
      {debtsLoaded && creditOptions?.types?.length > 0 && (
        <ManualCreditReportModal
          isFirstModalPage={isFirstModalPage}
          isManualMode={useManualEntry === true}
          popupOnRequest={popupOnRequest}
          debts={eligibleData}
          creditReport={creditReport}
          errors={errors}
          fields={fields}
          setFields={setFields}
          setErrors={setErrors}
          handleInputChange={handleInputChange}
          createCreditReport={handleCreateCreditReport}
          createDebts={handleCreateDebts}
          initialFieldSet={{ ...initialFieldSet, creditReport: creditReportId }}
          creditReportTypes={creditOptions?.types || []}
          creditReportStatuses={creditOptions?.statuses || []}
        />
      )}

      <div className="border border-divider bg-surface shadow-lg rounded-2xl pb-4 md:pb-6 w-full overflow-x-auto">
        <div className="flex items-center justify-between py-3 md:py-[18px] px-4 md:px-[32px]">
          <div className="font-bold text-lg md:text-xl-20 flex items-center">
            <CheckIcon />
            <div className="ml-2">Eligible Credit Lines</div>
          </div>
        </div>

        <div className="min-w-[600px]">
          <DataGrid
            sx={dataGridSx}
            rowHeight={80}
            columns={getColumns()}
            rows={eligibleData.map(item => ({ ...item, id: item._id }))}
            apiRef={apiRef}
            slots={{
              noRowsOverlay: () => (
                <div className="py-[20px] text-center text-text-secondary text-lg-18">
                  At this time, there are no existing eligible profiles available.
                </div>
              ),
            }}
            hideFooterPagination={true}
            disableRowSelectionOnClick
            disableColumnSelector
            checkboxSelection={false}
          />
        </div>
      </div>

      {ineligibleData.length > 0 && (
        <div className="border border-divider bg-surface shadow-lg rounded-2xl pb-4 md:pb-6 overflow-x-auto">
          <div className="flex items-center justify-between py-3 md:py-[18px] px-4 md:px-[32px]">
            <div className="font-bold text-lg md:text-xl-20 flex items-center gap-2">
              <BlockIcon />
              Ineligible credit lines
            </div>
          </div>

          <div className="min-w-[600px]">
            <DataGrid
              sx={{
                ...dataGridSx,
                '& .MuiDataGrid-virtualScroller': {
                  minHeight: 200,
                  overflow: 'visible !important',
                },
              }}
              rowHeight={80}
              columns={getColumns()}
              rows={ineligibleData.map(item => ({ ...item, id: item._id }))}
              apiRef={ineligibleApiRef}
              slots={{
                noRowsOverlay: () => (
                  <div className="py-[20px] text-center text-text-secondary text-lg-18">
                    The following credit lines are not supported by our relief programs.
                  </div>
                ),
              }}
              hideFooterPagination={true}
              disableRowSelectionOnClick
              disableColumnSelector
              checkboxSelection={false}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col-reverse sm:flex-row justify-between gap-3">
        <Button type="outlined" width="w-full sm:min-w-[156px] sm:w-auto" onClick={onBack}>
          Back
        </Button>
        <Button
          width="w-full sm:min-w-[156px] sm:w-auto"
          disabled={!selectedCreditLines?.length}
          onClick={onNext}
        >
          Save and Proceed
        </Button>
      </div>

      <ConfirmModal
        title="Are you sure to Delete this debt?"
        functionButton={removeDebt}
        modalButtonTitle="Delete"
        open={openDeleteModal.open}
        setOpen={(v) => setOpenDeleteModal({ ...openDeleteModal, open: v })}
        titleClasses="text-left ml-0"
      >
        You will not be able to cancel this action
      </ConfirmModal>

      <PopupWithActions
        anchor={anchor}
        open={popupOpen}
        handleClose={handleActionClose}
      >
        <div className="flex flex-col">
          <div
            onClick={handleEditClick}
            className="cursor-pointer border-b rounded-md border-b-divider p-[14px] text-text-primary hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
          >
            Edit
          </div>
          <div
            onClick={handleDeleteClick}
            className="cursor-pointer p-[14px] rounded-md text-primary-static-error-600 hover:bg-black/[0.04] dark:hover:bg-white/[0.04]"
          >
            Delete
          </div>
        </div>
      </PopupWithActions>

      <SelfEnrollmentUpdateDebtModal
        open={openUpdate.open}
        setOpen={(v) => setOpenUpdate({ ...openUpdate, open: v })}
        contactId={contactId}
        creditLine={openUpdate.creditLine}
        creditReportTypes={creditOptions?.types || []}
        creditReportStatuses={creditOptions?.statuses || []}
      />
    </div>
  )
}

export default SelfEnrollmentCreditTables
